'use client';

import { createRoot, type Root } from 'react-dom/client';

import { SearchBarWidget, type SearchSuggestionItem } from '@/features/search-bar';
import { createWidgetMount } from '@/shared/utils/widget-mount';
import {
  OBSERVED_SEARCH_BAR_WIDGET_ATTRIBUTES,
  readSearchBarWidgetProps,
} from './search-bar-element-props';
import type { SearchBarEventName } from './types';

function dispatchSearchBarEvent(
  element: HTMLElement,
  name: SearchBarEventName,
  detail?: unknown,
): boolean {
  return element.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
      cancelable: true,
    }),
  );
}

export class SearchBarWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_SEARCH_BAR_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private handleSubmit = (query: string) => {
    const proceedWithDefault = dispatchSearchBarEvent(this, 'adipa-search-bar-submit', { query });
    if (!proceedWithDefault) return;

    const props = readSearchBarWidgetProps(this);
    if (!props.resultsUrl) return;

    const suffix = props.resultsUrlSuffix ?? '';
    window.location.assign(`${props.resultsUrl}${encodeURIComponent(query)}${suffix}`);
  };

  private handleSelect = (item: SearchSuggestionItem) => {
    const proceedWithDefault = dispatchSearchBarEvent(this, 'adipa-search-bar-select', { item });
    if (!proceedWithDefault) return;

    if (item.href) {
      window.location.assign(item.href);
    }
  };

  private renderWidget() {
    if (!this.root) return;

    const props = readSearchBarWidgetProps(this);
    dispatchSearchBarEvent(this, 'adipa-search-bar-ready');

    this.root.render(
      <SearchBarWidget
        debounceMs={props.debounceMs}
        maxResults={props.maxResults}
        minLength={props.minLength}
        placeholder={props.placeholder}
        resultsUrl={props.resultsUrl}
        resultsUrlSuffix={props.resultsUrlSuffix}
        suggestUrl={props.suggestUrl}
        onSelect={this.handleSelect}
        onSubmit={this.handleSubmit}
      />,
    );
  }
}
